import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, File, Folder } from 'lucide-react';

export type SeoLinkNode = {
  name: string;
  href?: string;
  nodes?: SeoLinkNode[];
};

type SeoFilesystemItemProps = {
  node: SeoLinkNode;
  animated?: boolean;
};

function NodeLabel({ node }: { node: SeoLinkNode }) {
  if (node.href) {
    return (
      <a
        href={node.href}
        className="truncate text-sm font-medium text-neutral-800 underline-offset-2 hover:text-neutral-950 hover:underline"
      >
        {node.name}
      </a>
    );
  }

  return <span className="truncate text-sm font-medium text-neutral-800">{node.name}</span>;
}

export function SeoFilesystemItem({ node, animated = true }: SeoFilesystemItemProps) {
  const hasChildren = Boolean(node.nodes && node.nodes.length > 0);
  const [isOpen, setIsOpen] = useState(true);

  const chevron = animated ? (
    <motion.span
      animate={{ rotate: isOpen ? 90 : 0 }}
      transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
      className="flex shrink-0"
    >
      <ChevronRight className="size-4 text-neutral-400" />
    </motion.span>
  ) : (
    <ChevronRight className={`size-4 shrink-0 text-neutral-400 ${isOpen ? 'rotate-90' : ''}`} />
  );

  const children = node.nodes?.map((child) => (
    <SeoFilesystemItem node={child} key={`${child.name}-${child.href ?? 'folder'}`} animated={animated} />
  ));

  return (
    <li className="list-none">
      <span className="flex min-w-0 items-center gap-1.5 py-1">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setIsOpen((open) => !open)}
            className="-m-1 shrink-0 rounded p-1 hover:bg-neutral-100"
            aria-expanded={isOpen}
            aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
          >
            {chevron}
          </button>
        ) : (
          <span className="w-[22px] shrink-0" aria-hidden="true" />
        )}

        {hasChildren ? (
          <Folder
            className={`size-5 shrink-0 fill-neutral-200 text-neutral-700 ${
              hasChildren ? '' : 'ml-[22px]'
            }`}
          />
        ) : (
          <File className="size-5 shrink-0 text-neutral-900" />
        )}

        <NodeLabel node={node} />
      </span>

      {hasChildren &&
        (animated ? (
          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.ul
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ type: 'spring', bounce: 0, duration: 0.35 }}
                className="flex flex-col overflow-hidden pl-5"
              >
                {children}
              </motion.ul>
            )}
          </AnimatePresence>
        ) : (
          isOpen && <ul className="pl-5">{children}</ul>
        ))}
    </li>
  );
}

type SeoLinkTreeProps = {
  nodes: SeoLinkNode[];
};

export function SeoLinkTree({ nodes }: SeoLinkTreeProps) {
  return (
    <ul className="m-0 list-none p-0">
      {nodes.map((node) => (
        <SeoFilesystemItem node={node} key={`${node.name}-${node.href ?? 'root'}`} />
      ))}
    </ul>
  );
}
